import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import api from '@/utils/api';
import { CreditCard, User, Globe, FileText, ChevronRight, CheckCircle, Clock, Euro } from 'lucide-react';

const ICONS = { CreditCard, User, Globe, FileText };
const CATEGORY_LABELS = {
  documentos: 'Documentos', vistos: 'Vistos', certidoes: 'Certidões', notarial: 'Notarial'
};
const CATEGORY_COLORS = {
  documentos: 'bg-blue-50 text-blue-700 border-blue-200',
  vistos: 'bg-green-50 text-green-700 border-green-200',
  certidoes: 'bg-purple-50 text-purple-700 border-purple-200',
  notarial: 'bg-orange-50 text-orange-700 border-orange-200',
};

export default function Services() {
  const { t } = useLanguage();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get('/services').then(r => setServices(r.data || [])).finally(() => setLoading(false));
  }, []);

  const categories = [...new Set(services.map(s => s.category))];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-10">
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
            <Link to="/" className="hover:text-[#CC0000]">Início</Link>
            <ChevronRight size={12} />
            <span className="text-gray-600">{t('services.title')}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{t('services.title')}</h1>
          <p className="text-gray-500 text-sm md:text-base">{t('services.subtitle')}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map(i => <div key={i} className="h-56 bg-white rounded-xl animate-pulse" />)}
          </div>
        ) : (
          <>
            {categories.map(cat => (
              <div key={cat} className="mb-10">
                <div className="flex items-center gap-3 mb-5">
                  <h2 className="text-lg font-bold text-gray-800">{CATEGORY_LABELS[cat] || cat}</h2>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {services.filter(s => s.category === cat).map(svc => {
                    const Icon = ICONS[svc.icon] || FileText;
                    const colorClass = CATEGORY_COLORS[cat] || 'bg-gray-50 text-gray-700 border-gray-200';
                    return (
                      <div key={svc.id} data-testid={`service-${svc.id}`}
                        className={`bg-white border rounded-xl p-5 card-hover cursor-pointer transition-all ${selected === svc.id ? 'border-[#CC0000] shadow-md' : 'border-gray-100'}`}
                        onClick={() => setSelected(selected === svc.id ? null : svc.id)}>
                        <div className="flex items-start justify-between mb-3">
                          <div className={`p-2.5 rounded-lg border ${colorClass}`}>
                            <Icon size={20} />
                          </div>
                          <div className="text-right">
                            <div className="flex items-center gap-1 text-[#CC0000] font-bold text-lg">
                              <Euro size={14} />{svc.fee.toFixed(2)}
                            </div>
                            <span className="text-xs text-gray-400">{svc.currency}</span>
                          </div>
                        </div>
                        <h3 className="font-bold text-gray-900 mb-1.5">{svc.name}</h3>
                        <p className="text-sm text-gray-500 mb-3 leading-relaxed">{svc.description}</p>
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                          <Clock size={11} /> <span>{svc.processing_time}</span>
                        </div>

                        {selected === svc.id && (
                          <div className="mt-4 border-t border-gray-100 pt-4 slide-in">
                            <p className="text-xs font-semibold text-gray-700 mb-2">Documentos necessários:</p>
                            <ul className="space-y-1.5 mb-4">
                              {svc.requirements.map((req, i) => (
                                <li key={i} className="flex items-start gap-1.5 text-xs text-gray-600">
                                  <CheckCircle size={12} className="text-green-500 flex-shrink-0 mt-0.5" />
                                  {req}
                                </li>
                              ))}
                            </ul>
                            <Link to={`/pagamentos?service=${svc.category}`} data-testid={`pay-${svc.id}`}
                              className="flex items-center justify-center gap-2 w-full py-2.5 bg-[#CC0000] text-white text-sm font-semibold rounded-lg hover:bg-[#A30000] transition-colors">
                              <CreditCard size={14} /> {t('services.book')}
                            </Link>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </>
        )}

        {/* Info Box */}
        <div className="mt-8 bg-[#0A0A0A] text-white rounded-xl p-6">
          <h3 className="font-bold text-lg mb-3 text-[#FFD700]">Informações Importantes</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-300">
            <div><p className="font-semibold text-white mb-1">Agendamento Obrigatório</p><p>Recomendamos marcar consulta prévia para evitar esperas desnecessárias.</p></div>
            <div><p className="font-semibold text-white mb-1">Pagamento Online</p><p>Pague as taxas consulares de forma segura através do portal antes da visita.</p></div>
            <div><p className="font-semibold text-white mb-1">Documentos Originais</p><p>Todos os documentos devem ser apresentados em original e com cópia.</p></div>
          </div>
        </div>
      </div>
    </div>
  );
}
